/**
 * Config Context
 * Provides centralized configuration management across the application
 * Loads configs from API and makes them available via useConfig hook
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAllConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/config');
      
      // Transform array to object keyed by config key for easy access
      const configsMap = {};
      // Handle both response.data.data (from controller) and response.data.configs (if transformed)
      const configsArray = response.data?.data || response.data?.configs || [];
      
      if (Array.isArray(configsArray)) {
        configsArray.forEach(config => {
          if (config && config.key) {
            configsMap[config.key] = config;
          }
        });
      }
      
      setConfigs(configsMap);
      setError(null);
    } catch (err) {
      console.error('Failed to load configs:', err);
      
      // Handle 502 Bad Gateway specifically (server down/not reachable)
      let errorMessage;
      if (err.response?.status === 502) {
        errorMessage = 'Server is temporarily unavailable. Configuration will load when server is back online.';
      } else if (err.response?.status === 503) {
        errorMessage = 'Service is temporarily unavailable.';
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED') {
        errorMessage = 'Unable to connect to server.';
      } else {
        errorMessage = err.response?.data?.message || 
                      err.response?.data?.error || 
                      err.message || 
                      'Failed to load configuration';
      }
      
      setError(errorMessage);
      // Use empty configs as fallback - app can still work without configs
      setConfigs({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllConfigs();
  }, [loadAllConfigs]);

  const refreshConfig = async (key) => {
    try {
      const response = await api.get(`/config/${key}`);
      const config = response.data.data || response.data.config;
      if (config) {
        setConfigs(prev => ({
          ...prev,
          [key]: config
        }));
      }
    } catch (err) {
      console.error(`Failed to refresh config ${key}:`, err);
    }
  };

  const refreshAllConfigs = useCallback(() => {
    loadAllConfigs();
  }, [loadAllConfigs]);

  // Helper functions to get config values directly
  const getConfigValues = (key) => {
    const config = configs[key];
    if (!config) return [];
    return config.type === 'string_array' ? config.values : config.objectValues;
  };

  const value = {
    configs,
    loading,
    error,
    refreshConfig,
    refreshAllConfigs,
    getConfigValues
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
};

export default ConfigContext;
